import { useState } from "react";
import { msgid, ngettext, t } from "ttag";

import { Box, Checkbox, Stack, Text } from "metabase/ui";
import type { DatabaseUsageInfo } from "metabase-types/api";

interface ContentRemovalConfirmationProps {
  usageInfo: DatabaseUsageInfo;
  onChange: (isConfirmed: boolean) => void;
}

const ContentRemovalConfirmation = ({
  usageInfo,
  onChange,
}: ContentRemovalConfirmationProps) => {
  const { question, dataset, metric, segment, transform } = usageInfo;

  const [confirmations, setConfirmations] = useState({
    question: question === 0,
    dataset: dataset === 0,
    metric: metric === 0,
    segment: segment === 0,
    transform: transform === 0,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    const updatedConfirmations = { ...confirmations, [name]: checked };
    setConfirmations(updatedConfirmations);

    const isConfirmed = Object.values(updatedConfirmations).every(Boolean);
    onChange(isConfirmed);
  };

  return (
    <Box
      component="fieldset"
      bd="none"
      p={0}
      m={0}
      style={{ minInlineSize: 0 }}
    >
      <Box component="legend" mb="sm">
        <Text size="md" fw="bold">
          {t`Content that will be deleted`}
        </Text>
      </Box>
      <Stack gap="md">
        {question > 0 && (
          <Checkbox
            label={ngettext(
              msgid`Delete ${question} saved question`,
              `Delete ${question} saved questions`,
              question,
            )}
            name="question"
            checked={confirmations["question"]}
            onChange={handleChange}
          />
        )}
        {dataset > 0 && (
          <Checkbox
            label={ngettext(
              msgid`Delete ${dataset} model`,
              `Delete ${dataset} models`,
              dataset,
            )}
            name="dataset"
            checked={confirmations["dataset"]}
            onChange={handleChange}
          />
        )}
        {metric > 0 && (
          <Checkbox
            label={ngettext(
              msgid`Delete ${metric} metric`,
              `Delete ${metric} metrics`,
              metric,
            )}
            name="metric"
            checked={confirmations["metric"]}
            onChange={handleChange}
          />
        )}
        {segment > 0 && (
          <Checkbox
            label={ngettext(
              msgid`Delete ${segment} segment`,
              `Delete ${segment} segments`,
              segment,
            )}
            name="segment"
            checked={confirmations["segment"]}
            onChange={handleChange}
          />
        )}
        {transform > 0 && (
          <Checkbox
            label={ngettext(
              msgid`${transform} transform will stop working`,
              `${transform} transforms will stop working`,
              transform,
            )}
            name="transform"
            checked={confirmations["transform"]}
            onChange={handleChange}
          />
        )}
      </Stack>
    </Box>
  );
};

// eslint-disable-next-line import/no-default-export -- deprecated usage
export default ContentRemovalConfirmation;
